import { CalendarIcon, LinkIcon, LocationIcon } from '@/assets/icons'
import { countLabel } from '@/utils/plural'
import TransportModeIcon from '@components/TransportModeIcon'
import { useIsNarrow } from '@hooks'
import { RailConnector } from '@pages/Itinerary/timelineRail.styles'
import React from 'react'
import BentoTile from './BentoTile'
import {
	ItinerarySection,
	MOBILE_PREVIEW_EVENTS,
	OverviewDay,
	OverviewEvent,
	TileState,
} from './core'
import EmptyState from './EmptyState'
import { BENTO_COLLAPSE_PX } from './sectionLayout'
import SectionTeaser from './SectionTeaser'
import TagPill from './TagPill'
import {
	DayColumn,
	DayDate,
	DayHead,
	DayName,
	Dot,
	EventCard,
	EventTitle,
	MetaRow,
	PreviewRow,
	Rail,
	SEGMENT_ANCHOR,
	SegmentIcon,
	SegmentLabel,
	SegmentRail,
	SegmentRow,
	Stop,
	STOP_ANCHOR,
	Stops,
} from './ItineraryTile.styles'
import { iconSizes } from '@/theme/tokens'

// Small "has a note" glyph (three lines), mirroring the itinerary card's meta row.
const NoteGlyph: React.FC = () => (
	<svg
		width={14}
		height={14}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={2}
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<line x1="4" y1="7" x2="20" y2="7" />
		<line x1="4" y1="12" x2="20" y2="12" />
		<line x1="4" y1="17" x2="14" y2="17" />
	</svg>
)

const EventMeta: React.FC<{ event: OverviewEvent }> = ({ event }) => {
	if (!event.hasLocation && !event.hasNote && !event.hasLink) return null
	return (
		<MetaRow aria-hidden="true">
			{event.hasLocation && <LocationIcon sx={{ fontSize: iconSizes.xs }} />}
			{event.hasNote && <NoteGlyph />}
			{event.hasLink && <LinkIcon sx={{ fontSize: iconSizes.xs }} />}
		</MetaRow>
	)
}

const DayPreview: React.FC<{ day: OverviewDay; maxEvents: number }> = ({ day, maxEvents }) => {
	const events = day.events.slice(0, maxEvents)
	return (
		<DayColumn>
			<DayHead>
				<DayName>{day.dayName}</DayName>
				<DayDate>{day.dateLabel}</DayDate>
			</DayHead>
			<Stops>
				{events.map((event, index) => {
					const isLast = index === events.length - 1
					// Travel to the next card — never after the last visible one.
					const segment = isLast ? undefined : event.travelAfter
					return (
						<React.Fragment key={event.id}>
							<Stop>
								<Rail aria-hidden="true">
									{/* One dash run per gap, aimed at whatever follows: the
									    travel chip's centre or the next dot's centre. */}
									{!isLast && (
										<RailConnector
											$from={STOP_ANCHOR}
											$into={segment ? SEGMENT_ANCHOR : STOP_ANCHOR}
										/>
									)}
									<Dot />
								</Rail>
								<EventCard>
									<TagPill>{event.categoryLabel}</TagPill>
									<EventTitle>{event.title}</EventTitle>
									<EventMeta event={event} />
								</EventCard>
							</Stop>
							{segment && (
								<SegmentRow>
									<SegmentRail aria-hidden="true">
										<RailConnector $from={SEGMENT_ANCHOR} $into={STOP_ANCHOR} />
										<SegmentIcon>
											<TransportModeIcon mode={segment.mode} />
										</SegmentIcon>
									</SegmentRail>
									<SegmentLabel>{segment.label}</SegmentLabel>
								</SegmentRow>
							)}
						</React.Fragment>
					)
				})}
			</Stops>
		</DayColumn>
	)
}

interface ItineraryTileProps {
	section: ItinerarySection
	/** Resolved tile state from the shared state machine (see `resolveTileState`). */
	state: TileState
	/** Navigate to the full itinerary. */
	onNavigate: () => void
	/** Open the destinations editor (used by the no-dates empty state). */
	onSetDates: () => void
	/** Render a skeleton while the events query loads. */
	loading?: boolean
}

/**
 * The tall itinerary tile. Its preview mirrors the itinerary screen's timeline
 * (rail, dots, cards, travel-segment rows). `empty` refines further: no dates →
 * a "set trip dates" affordance; dates but no events → a zero-state.
 */
const ItineraryTile: React.FC<ItineraryTileProps> = ({ section, state, onNavigate, onSetDates, loading }) => {
	const { hasDates, dayCount, previewDays } = section
	// Below the bento collapse the tile is content-sized (no grid clip), so the
	// preview caps itself; on desktop it renders everything and lets CSS clip.
	const isNarrow = useIsNarrow(BENTO_COLLAPSE_PX)
	const maxEvents = isNarrow ? MOBILE_PREVIEW_EVENTS : Number.POSITIVE_INFINITY

	const count = hasDates ? `${countLabel(dayCount, 'day')}` : undefined

	if (state === 'teaser') {
		return (
			<SectionTeaser
				icon={<CalendarIcon sx={{ fontSize: iconSizes.sm }} />}
				label="Itinerary"
				noun="the itinerary"
				count={count}
				colSpan={6}
				rowSpan={2}
				loading={loading}
			/>
		)
	}

	const footer = hasDates ? 'Open the full itinerary' : 'Set trip dates'
	const onClick = hasDates ? onNavigate : onSetDates
	const ariaLabel = hasDates ? 'Open the full itinerary' : 'Set your trip dates'

	let body: React.ReactNode
	if (state === 'detail') {
		body = (
			<PreviewRow>
				{previewDays.map(day => (
					<DayPreview key={day.id} day={day} maxEvents={maxEvents} />
				))}
			</PreviewRow>
		)
	} else if (!hasDates) {
		body = (
			<EmptyState
				art="itinerary"
				text="Set your itinerary dates to start building your itinerary."
			/>
		)
	} else {
		body = (
			<EmptyState
				art="itinerary"
				text="Plan your days so everyone knows what's happening."
			/>
		)
	}

	return (
		<BentoTile
			icon={<CalendarIcon sx={{ fontSize: iconSizes.sm }} />}
			label="Itinerary"
			count={count}
			footer={footer}
			ariaLabel={ariaLabel}
			colSpan={6}
			rowSpan={2}
			onClick={onClick}
			loading={loading}
			skeletonRows={5}
		>
			{body}
		</BentoTile>
	)
}

export default ItineraryTile
